/**
 * An empty state.
 *
 * Two parts, both required: what is not here, and what to do about it. A bare
 * "No results" tells a user the query ran and nothing else — whether they
 * should widen a filter, add the first record, or report a bug is left to them
 * to guess. Making the second line structural is what stops it being dropped.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.5.9, §4.5
 */
import { cn } from '../../lib/cn';

export interface EmptyProps {
  /** What is not here. A short noun phrase — "No pilots yet". */
  title: React.ReactNode;
  /** What the reader can do about it. */
  children: React.ReactNode;
  /** An action, where one would actually resolve the emptiness. */
  action?: React.ReactNode;
  className?: string;
}

export function Empty({
  title,
  children,
  action,
  className,
}: EmptyProps): React.JSX.Element {
  return (
    <div className={cn('text-body px-s4 py-s6 text-center font-light', className)}>
      <b className="font-display text-navy-text mb-1.5 block text-base font-extrabold">
        {title}
      </b>
      {children}
      {action !== undefined && <div className="mt-s3">{action}</div>}
    </div>
  );
}
